import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Clock, Minus, Pause, Pencil, Plus, Timer, Trophy } from 'lucide-react';
import {
  clamp, describeSet, extraSetIndex, formatClock, formatKg, formatReps, nextSetIndex, parseDecimal, parseIntSafe, plannedSetCount, plural,
  safeStorage, suggestSet, trackingKind, warmupPlan, warmupSets, weightStep, workSets,
} from './playerLogic';

// O registo das séries de um exercício — lê-se num relance:
//   · as CONCLUÍDAS ficam compactas, com o número à vista e um visto verde, e dizem «Concluída»;
//   · a série ATUAL é a única aberta: os campos (repetições, carga, esforço) estão à frente, prontos a editar,
//     e o botão grande de registar é o único aceso;
//   · as seguintes estão ESBATIDAS e dizem «Planeada», com o que o plano prescreve (sem inventar nada).
// Cada tipo de registo pede só o que lhe diz respeito: um exercício por tempo não tem peso nem repetições.

const RPE_CHOICES = [5, 6, 7, 8, 9, 10];

const toDraft = (suggestion) => ({
  reps: suggestion.reps == null ? '' : String(suggestion.reps),
  weightKg: suggestion.weightKg == null ? '' : formatKg(suggestion.weightKg),
  durationSeconds: suggestion.durationSeconds == null ? '' : String(suggestion.durationSeconds),
  rpe: null,
});

function Stepper({ label, unit, value, onChange, onStep, inputMode = 'numeric', testId, min, hint }) {
  return (
    <div className="wp-stepper">
      <label className="wp-stepper-label" htmlFor={testId}>{label}</label>
      <div className="wp-stepper-value">
        <input id={testId} data-testid={testId} className="wp-stepper-input" type="text" inputMode={inputMode} autoComplete="off" enterKeyHint="done"
          value={value} onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()} aria-describedby={hint ? `${testId}-hint` : undefined} />
        <span className="wp-stepper-unit">{unit}</span>
      </div>
      <div className="wp-stepper-btns">
        <button type="button" className="wp-step-btn" onClick={() => onStep(-1)} aria-label={`Diminuir ${label}`} disabled={min != null && Number(parseDecimal(value)) <= min}><Minus size={22} aria-hidden="true" /></button>
        <button type="button" className="wp-step-btn" onClick={() => onStep(1)} aria-label={`Aumentar ${label}`}><Plus size={22} aria-hidden="true" /></button>
      </div>
      {hint ? <span id={`${testId}-hint`} className="wp-stepper-hint">{hint}</span> : null}
    </div>
  );
}

/** Um cronómetro para os exercícios por tempo (prancha, corrida…): inicia, pára e preenche o tempo. */
function Stopwatch({ onStop }) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 250);
    return () => clearInterval(id);
  }, [running]);

  const toggle = () => {
    if (running) {
      const seconds = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
      setRunning(false);
      setElapsed(seconds);
      onStop(seconds);
    } else {
      startedAt.current = Date.now();
      setElapsed(0);
      setRunning(true);
    }
  };

  return (
    <button type="button" className={`wp-btn wp-btn-ghost wp-stopwatch ${running ? 'is-running' : ''}`} onClick={toggle} data-testid="stopwatch">
      {running ? <Pause size={18} aria-hidden="true" /> : <Timer size={18} aria-hidden="true" />}
      {running ? `A contar… ${formatClock(elapsed)} · parar` : 'Cronómetro'}
    </button>
  );
}

/** Corrigir ou apagar uma série já enviada ao servidor (só com ligação): valores novos, ou apagar por engano. */
function SetEditor({ set, kind, onSave, onDelete, onCancel, busy }) {
  const [reps, setReps] = useState(set.reps ? String(set.reps) : '');
  const [weight, setWeight] = useState(set.weightKg == null ? '' : formatKg(set.weightKg));
  const [seconds, setSeconds] = useState(set.durationSeconds == null ? '' : String(set.durationSeconds));
  const [rpe, setRpe] = useState(set.rpe ? String(set.rpe) : '');
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = async () => {
    const change = {};
    if (kind.usesTime) {
      const s = parseIntSafe(seconds);
      if (s == null || s <= 0) { setError('Indique o tempo em segundos.'); return; }
      change.durationSeconds = s;
    } else {
      const r = parseIntSafe(reps);
      if (r == null || r < 0) { setError('Indique as repetições.'); return; }
      change.reps = r;
      if (kind.usesWeight || kind.optionalWeight) {
        const w = weight === '' ? null : parseDecimal(weight);
        if (weight !== '' && (w == null || w < 0)) { setError('A carga tem de ser um número.'); return; }
        if (w != null) change.weightKg = w;
      }
    }
    if (rpe !== '') {
      const v = parseDecimal(rpe);
      if (v == null || v < 1 || v > 10) { setError('O esforço (RPE) vai de 1 a 10.'); return; }
      change.rpe = v;
    }
    const result = await onSave(change);
    if (!result.ok) setError(result.message);
  };

  const remove = async () => {
    const result = await onDelete();
    if (!result.ok) { setError(result.message); setConfirmDelete(false); }
  };

  return (
    <div className="wp-set-editor" data-testid={`edit-set-${set.setIndex}`}>
      <div className="wp-set-editor-fields">
        {kind.usesTime ? (
          <label>Tempo (s)<input className="wp-stepper-input" inputMode="numeric" value={seconds} onChange={(e) => { setSeconds(e.target.value); setError(null); }} aria-label="Tempo em segundos" /></label>
        ) : (
          <>
            {kind.usesWeight || kind.optionalWeight ? (
              <label>Carga (kg)<input className="wp-stepper-input" inputMode="decimal" value={weight} onChange={(e) => { setWeight(e.target.value); setError(null); }} aria-label="Carga em quilos" /></label>
            ) : null}
            <label>Repetições<input className="wp-stepper-input" inputMode="numeric" value={reps} onChange={(e) => { setReps(e.target.value); setError(null); }} aria-label="Repetições" /></label>
          </>
        )}
        <label>RPE<input className="wp-stepper-input" inputMode="decimal" value={rpe} onChange={(e) => { setRpe(e.target.value); setError(null); }} aria-label="Esforço percebido" /></label>
      </div>
      {error ? <p className="wp-error" role="alert">{error}</p> : null}
      {confirmDelete ? (
        <div className="wp-set-editor-actions">
          <span>Apagar a série {set.setIndex}?</span>
          <button type="button" className="wp-btn wp-btn-ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>Não</button>
          <button type="button" className="wp-btn wp-btn-primary" onClick={remove} disabled={busy} data-testid="confirm-delete-set">Apagar</button>
        </div>
      ) : (
        <div className="wp-set-editor-actions">
          <button type="button" className="wp-btn wp-btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button type="button" className="wp-btn wp-btn-ghost" onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="delete-set">Apagar série</button>
          <button type="button" className="wp-btn wp-btn-primary" onClick={save} disabled={busy} data-testid="save-set-edit">Guardar</button>
        </div>
      )}
    </div>
  );
}

export default function SetLogger({ exercise, onLog, onEditSet, disabled = false, confirmRef }) {
  const kind = trackingKind(exercise.trackingType);
  const [editing, setEditing] = useState(null);   // setIndex em correção
  const [editBusy, setEditBusy] = useState(false);
  const [extra, setExtra] = useState(false);
  const [showRpe, setShowRpe] = useState(false);
  const [errors, setErrors] = useState([]);
  const lock = useRef(false);
  const activeRef = useRef(null);

  // Aquecimento: proposto a partir da carga de trabalho, ou já feito. Saltar é do atleta e fica guardado no
  // aparelho (por exercício), para não voltar a aparecer em cada visita ao ecrã.
  const warmupKey = `warmup.skip.${exercise.id}`;
  const proposedWarmups = warmupPlan(exercise);
  const doneWarmups = warmupSets(exercise);
  const warmupRows = proposedWarmups.length > 0
    ? proposedWarmups
    : doneWarmups.map((s) => ({ setIndex: s.setIndex, reps: s.reps, weightKg: s.weightKg }));
  const [warmupSkipped, setWarmupSkipped] = useState(() => safeStorage.get(warmupKey) === true);
  useEffect(() => { setWarmupSkipped(safeStorage.get(warmupKey) === true); }, [warmupKey]);

  const skipWarmup = () => { safeStorage.set(warmupKey, true); setWarmupSkipped(true); };
  const showWarmup = () => { safeStorage.remove(warmupKey); setWarmupSkipped(false); };
  const logWarmup = (item) => {
    if (disabled) return;
    const result = onLog({ isWarmup: true, setIndex: item.setIndex, reps: String(item.reps), weightKg: formatKg(item.weightKg) });
    if (!result?.ok && !result?.duplicate) setErrors(result?.errors || ['Não foi possível registar o aquecimento.']);
  };

  const planned = plannedSetCount(exercise);
  const open = nextSetIndex(exercise);
  const activeIndex = open ?? (extra ? extraSetIndex(exercise) : null);
  const closed = exercise.status === 'Skipped' || exercise.status === 'Completed';

  const [draft, setDraft] = useState(() => toDraft(suggestSet(exercise, activeIndex ?? 1)));
  const draftFor = useRef(`${exercise.id}:${activeIndex}`);

  // Quando a série ativa muda (acabou a anterior, outro exercício), abre com os valores novos.
  useEffect(() => {
    const key = `${exercise.id}:${activeIndex}`;
    if (draftFor.current !== key) {
      draftFor.current = key;
      setDraft(toDraft(suggestSet(exercise, activeIndex ?? 1)));
      setErrors([]);
      setShowRpe(false);
    }
  }, [exercise, activeIndex]);

  useEffect(() => { setExtra(false); }, [exercise.id]);

  // A série ativa só é trazida à vista quando AVANÇA dentro do mesmo exercício (acabou de registar e a
  // próxima ficou abaixo). Nunca ao abrir o exercício: aí o que interessa ver é o topo — a imagem e o nome.
  const lastActive = useRef({ exerciseId: null, index: null });
  useEffect(() => {
    const prev = lastActive.current || {};
    lastActive.current = { exerciseId: exercise.id, index: activeIndex };
    const sameExercise = prev.exerciseId === exercise.id;
    if (!sameExercise || prev.index == null || activeIndex == null || activeIndex <= prev.index) return;
    const el = activeRef.current;
    if (!el || typeof el.getBoundingClientRect !== 'function' || typeof el.scrollIntoView !== 'function') return;
    const rect = el.getBoundingClientRect();
    const nav = document.querySelector('.wp-player-nav');
    const limit = (nav ? nav.getBoundingClientRect().top : window.innerHeight) - 12;
    if (rect.top > limit) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeIndex, exercise.id]);

  const update = useCallback((patch) => { setDraft((d) => ({ ...d, ...patch })); setErrors([]); }, []);

  const stepWeight = (dir) => {
    const current = parseDecimal(draft.weightKg) ?? 0;
    const next = clamp(Math.round((current + dir * weightStep(dir < 0 ? current - 0.01 : current)) * 100) / 100, 0, 1000);
    update({ weightKg: formatKg(next) });
  };
  const stepReps = (dir) => update({ reps: String(clamp((parseIntSafe(draft.reps) ?? 0) + dir, 0, 500)) });
  const stepSeconds = (dir) => update({ durationSeconds: String(clamp((parseIntSafe(draft.durationSeconds) ?? 0) + dir * 5, 0, 86400)) });

  const confirm = () => {
    if (lock.current || disabled || activeIndex == null) return;
    lock.current = true;
    setTimeout(() => { lock.current = false; }, 500);   // dois toques seguidos não são duas séries (e o servidor ainda recusa a repetida)
    const result = onLog({ ...draft, setIndex: activeIndex });
    if (!result.ok && !result.duplicate) setErrors(result.errors || ['Não foi possível registar a série.']);
    if (result.ok) { setExtra(false); setShowRpe(false); }
  };

  // A barra de baixo do player pede o registo da série por aqui (o rascunho — carga, reps, RPE — vive neste
  // componente; o botão que o dispara está no cartão no ecrã largo e na barra no telemóvel).
  if (confirmRef) confirmRef.current = activeIndex == null || closed ? null : confirm;

  const last = exercise.lastValues;
  const lastForSet = (last?.sets || []).find((s) => s.setIndex === activeIndex) || (last?.sets || [])[0];
  const rows = Array.from({ length: Math.max(planned, extra && activeIndex ? activeIndex : 0) }, (_, i) => i + 1);
  const doneBy = new Map(workSets(exercise).map((s) => [s.setIndex, s]));
  const doneCount = doneBy.size;
  const plannedLabel = exercise.prescription?.setsTarget;
  const warmupDoneBy = new Map(doneWarmups.map((s) => [s.setIndex, s]));

  return (
    <section className="wp-sets" aria-label="Séries" data-testid="set-logger">
      <div className="wp-sets-head">
        <h2 className="wp-sets-title">Séries</h2>
        <span className="wp-sets-count" data-testid="sets-count">
          {plannedLabel != null ? `${doneCount} de ${Math.max(plannedLabel, doneCount)} registadas` : plural(doneCount, 'série registada', 'séries registadas')}
        </span>
      </div>

      {/* O aquecimento vem ANTES das séries a sério e diz o que é: não conta para nada do treino. Um toque salta-o. */}
      {warmupRows.length > 0 && !closed ? (
        warmupSkipped && doneWarmups.length === 0 ? (
          <p className="wp-warmup-skipped" data-testid="warmup-skipped">
            Aquecimento saltado.
            <button type="button" className="wp-link-btn" onClick={showWarmup} data-testid="show-warmup">Mostrar aquecimento</button>
          </p>
        ) : (
          <div className="wp-warmup" data-testid="warmup" aria-label="Aquecimento">
            <div className="wp-warmup-head">
              <h3 className="wp-warmup-title">Aquecimento</h3>
              <button type="button" className="wp-btn wp-btn-ghost wp-warmup-skip" onClick={skipWarmup} disabled={doneWarmups.length > 0} data-testid="skip-warmup">
                Saltar aquecimento
              </button>
            </div>
            <p className="wp-note">Não conta para as séries registadas, o volume nem os recordes.</p>
            <ul className="wp-warmup-list">
              {warmupRows.map((item) => {
                const done = warmupDoneBy.get(item.setIndex);
                return (
                  <li key={item.setIndex} className={`wp-warmup-row ${done ? 'is-done' : ''}`} data-testid={`warmup-${item.setIndex}`}>
                    <span className="wp-warmup-label">Aquecimento {item.setIndex}</span>
                    <span className="wp-warmup-value">{formatKg(item.weightKg)} kg × {item.reps}</span>
                    {done ? (
                      <span className="wp-warmup-done"><Check size={14} strokeWidth={3} aria-hidden="true" /> Feito</span>
                    ) : (
                      <button type="button" className="wp-btn wp-btn-ghost wp-warmup-done-btn" onClick={() => logWarmup(item)} disabled={disabled} data-testid={`warmup-done-${item.setIndex}`}>
                        Feito
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )
      ) : null}

      <ol className="wp-set-list">
        {rows.map((i) => {
          const done = doneBy.get(i);
          if (done) {
            const canEdit = !!onEditSet && !done.pending && !!done.id && exercise.status !== 'Skipped';
            return (
              <li key={i} className={`wp-set is-done ${done.pending ? 'is-pending' : ''}`} data-testid={`set-${i}`} data-state="done">
                <span className="wp-set-num" aria-hidden="true">
                  {i}
                  {done.pending ? <Clock size={13} className="wp-set-num-tick" /> : <Check size={13} strokeWidth={3.5} className="wp-set-num-tick" />}
                </span>
                <span className="wp-set-text">
                  <span className="wp-set-value"><span className="wp-visually-hidden">Série {i} concluída: </span>{describeSet(done, exercise.trackingType)}{kind.usesTime ? '' : ' reps'}</span>
                  <span className="wp-set-state" aria-hidden="true">Concluída</span>
                </span>
                {done.rpe ? <span className="wp-set-rpe">RPE {formatKg(done.rpe)}</span> : null}
                {/* O aviso de recorde é do SERVIDOR (a série bateu o melhor anterior dele neste exercício); sem histórico
                    ele não vem, e a série não diz nada — nunca se inventa um recorde. */}
                {!done.pending && done.isRecord ? (
                  <span className="wp-set-record" data-testid={`set-record-${i}`}>
                    <Trophy size={13} aria-hidden="true" /> Recorde pessoal
                  </span>
                ) : null}
                {done.pending ? <span className="wp-set-pending">por enviar</span> : null}
                {canEdit && editing !== i ? (
                  <button type="button" className="wp-link-btn wp-set-edit" onClick={() => setEditing(i)} aria-label={`Corrigir a série ${i}`} data-testid={`edit-open-${i}`}>
                    <Pencil size={14} aria-hidden="true" /> Corrigir
                  </button>
                ) : null}
                {canEdit && editing === i ? (
                  <SetEditor
                    set={done} kind={kind} busy={editBusy}
                    onCancel={() => setEditing(null)}
                    onSave={async (change) => {
                      setEditBusy(true);
                      const result = await onEditSet(done, change);
                      setEditBusy(false);
                      if (result.ok) setEditing(null);
                      return result;
                    }}
                    onDelete={async () => {
                      setEditBusy(true);
                      const result = await onEditSet(done, 'delete');
                      setEditBusy(false);
                      if (result.ok) setEditing(null);
                      return result;
                    }}
                  />
                ) : null}
              </li>
            );
          }
          if (i === activeIndex && !closed) {
            return (
              <li key={i} ref={activeRef} className="wp-set is-active" data-testid="active-set" data-state="active" aria-current="step">
                <div className="wp-set-head">
                  <span className="wp-set-title"><span className="wp-set-state-pill">Agora</span> Série {i}<span className="wp-set-of"> de {Math.max(planned, i)}</span></span>
                  {lastForSet ? <span className="wp-set-last" data-testid="last-hint">Última vez: {describeSet(lastForSet, exercise.trackingType)}{kind.usesTime ? '' : ' reps'}</span> : null}
                </div>

                <div className={`wp-steppers ${kind.usesWeight || kind.optionalWeight ? 'two' : 'one'}`}>
                  {kind.usesTime ? (
                    <Stepper label="Tempo" unit="s" testId="field-seconds" value={draft.durationSeconds} onChange={(v) => update({ durationSeconds: v })} onStep={stepSeconds} min={0}
                      hint={parseIntSafe(draft.durationSeconds) ? formatClock(parseIntSafe(draft.durationSeconds)) : undefined} />
                  ) : (
                    <>
                      {kind.usesWeight || kind.optionalWeight ? (
                        <Stepper label="Carga" unit="kg" testId="field-weight" inputMode="decimal" value={draft.weightKg} onChange={(v) => update({ weightKg: v })} onStep={stepWeight} min={0} />
                      ) : null}
                      <Stepper label="Repetições" unit="reps" testId="field-reps" value={draft.reps} onChange={(v) => update({ reps: v })} onStep={stepReps} min={0}
                        hint={exercise.prescription?.repsMin != null ? `Objetivo: ${formatReps(exercise.prescription)}` : undefined} />
                    </>
                  )}
                </div>

                {kind.usesTime ? <Stopwatch onStop={(seconds) => update({ durationSeconds: String(seconds) })} /> : null}
                {kind.type === 'distance' ? <p className="wp-note">A distância ainda não se regista: use o tempo.</p> : null}

                <div className="wp-set-log">
                  <button type="button" className={`wp-rpe-toggle ${draft.rpe ? 'is-set' : ''}`} onClick={() => setShowRpe((v) => !v)} aria-expanded={showRpe} aria-label={draft.rpe ? `Esforço percebido: RPE ${draft.rpe}` : 'Esforço percebido (RPE), opcional'} data-testid="rpe-toggle">
                    <span className="wp-rpe-toggle-label">Esforço (RPE)</span>
                    <span className="wp-rpe-toggle-value" aria-hidden="true">{draft.rpe || 'Opcional'}</span>
                  </button>

                  {showRpe ? (
                    <div className="wp-rpe-row" role="radiogroup" aria-label="Esforço percebido (RPE)">
                      {RPE_CHOICES.map((v) => (
                        <button key={v} type="button" role="radio" aria-checked={draft.rpe === v} className={`wp-rpe-chip ${draft.rpe === v ? 'is-on' : ''}`} onClick={() => update({ rpe: draft.rpe === v ? null : v })}>{v}</button>
                      ))}
                    </div>
                  ) : null}

                  {errors.length > 0 ? <p className="wp-error" role="alert">{errors[0]}</p> : null}
                </div>

                {/* A ação primária, sozinha num bloco que fica colado ao fundo do ecrã (ver CSS `.wp-set-cta`):
                    o polegar chega-lhe sempre, mesmo quando o atleta está a ver a imagem lá em cima. */}
                <div className="wp-set-cta">
                  <button type="button" className="wp-btn wp-btn-primary wp-btn-xl wp-confirm-set" onClick={confirm} disabled={disabled} data-testid="confirm-set">
                    <Check size={24} strokeWidth={3} aria-hidden="true" /> Concluir série {i}
                  </button>
                </div>
              </li>
            );
          }
          return (
            <li key={i} className="wp-set is-upcoming" data-testid={`set-${i}`} data-state="planned">
              <span className="wp-set-num" aria-hidden="true">{i}</span>
              <span className="wp-set-text">
                <span className="wp-set-value">{upcomingText(exercise, i)}</span>
                <span className="wp-set-state">Planeada</span>
              </span>
            </li>
          );
        })}
      </ol>

      {!closed && open == null && !extra ? (
        <button type="button" className="wp-btn wp-btn-ghost wp-extra-set" onClick={() => setExtra(true)} data-testid="extra-set"><Plus size={18} aria-hidden="true" /> Série extra</button>
      ) : null}
    </section>
  );
}

const upcomingText = (exercise, index) => {
  const kind = trackingKind(exercise.trackingType);
  const p = exercise.prescription || {};
  const s = suggestSet(exercise, index);
  if (kind.usesTime) return `Objetivo ${formatClock(s.durationSeconds || 0)}`;
  const target = formatReps(p) || (s.reps != null ? String(s.reps) : '');
  const kg = s.weightKg != null && s.weightKg > 0 && kind.usesWeight ? `${formatKg(s.weightKg)} kg × ` : '';
  return target ? `${kg}${target} reps` : 'Por fazer';
};

